import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { LeadContextItem } from "@/components/LeadFormModal";

export interface BookingResult {
  startUtc: string;
  email: string;
}

interface BookDemoContextValue {
  contextItems: LeadContextItem[] | undefined;
  contextPayload: Record<string, unknown> | null;
  setContext: (
    items: LeadContextItem[] | undefined,
    payload: Record<string, unknown> | null,
  ) => void;
  bookingResult: BookingResult | null;
  setBookingResult: (result: BookingResult | null) => void;
  reset: () => void;
}

const BookDemoContext = createContext<BookDemoContextValue | null>(null);

export function BookDemoProvider({ children }: { children: ReactNode }) {
  const [contextItems, setContextItems] = useState<LeadContextItem[] | undefined>(
    undefined,
  );
  const [contextPayload, setContextPayload] = useState<Record<string, unknown> | null>(
    null,
  );
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);

  const value = useMemo<BookDemoContextValue>(
    () => ({
      contextItems,
      contextPayload,
      setContext: (items, payload) => {
        setContextItems(items);
        setContextPayload(payload);
      },
      bookingResult,
      setBookingResult,
      reset: () => {
        setContextItems(undefined);
        setContextPayload(null);
        setBookingResult(null);
      },
    }),
    [contextItems, contextPayload, bookingResult],
  );

  return (
    <BookDemoContext.Provider value={value}>{children}</BookDemoContext.Provider>
  );
}

export function useBookDemo(): BookDemoContextValue {
  const ctx = useContext(BookDemoContext);
  if (!ctx) {
    throw new Error("useBookDemo must be used within a BookDemoProvider");
  }
  return ctx;
}
